import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import usePoll from "../../hooks/usePoll";
import { useSocketEvent } from "../../hooks/useSocketEvent";
import { showToast } from "../errors";
import { TagIcon } from "../../utils/tagIcons";
import { publishNewLeadCount } from "./osrAlertStore";
import { QUIET_ACTIONS } from "./useOsrBoard";
import { fmtSpan, handedInBy, osrFetch, secondsSince, sourceLabel, waitingLevel } from "./osrShared";
import useOsrPrefs, { readOsrPrefs } from "./osrPrefs";
import { OSR_PATH, OSR_WORDING, leadLink } from "./osrWording";
import { useNow } from "./useCountdown";
import { chime } from "../../utils/chime";
import "./OsrBits.css";
import "./OsrAlerts.css";

const POLL_MS = 60000;
const MAX_CARDS = 3;
const DISMISSED_KEY = "chariot.osr.alerts.dismissed";

const readDismissed = () => {
  try {
    const raw = sessionStorage.getItem(DISMISSED_KEY);
    return new Set(raw ? JSON.parse(raw) : []);
  } catch {
    return new Set();
  }
};
const writeDismissed = (set) => {
  try {
    sessionStorage.setItem(DISMISSED_KEY, JSON.stringify([...set].slice(-200)));
  } catch {
    /* storage blocked — dismissals last until the page reloads */
  }
};

/**
 * Lead alerts, on every page of the app, for people who work leads.
 *
 *   A new lead          → people who can claim: a card with Capture on it,
 *                         and a chime (once per lead, never for a lead you
 *                         handed in yourself, off if you switched it off).
 *   Waiting too long    → people who reassign and cannot claim: the same
 *                         lead, as a heads-up.
 *   Claimed, not called → the owner.
 *   Follow-up due       → the owner.
 *   Wrote in again      → the owner, when their customer sends another
 *                         request while the lead is open.
 *   Sales Sheet?        → the owner, when someone else sent a Sales Sheet
 *                         for their customer: it waits on the lead, not
 *                         marked Sold, until they say whose sale it is.
 *   Spam to review      → people who decide spam, when a lead is flagged.
 *   Not spam            → the owner, when a manager sends a flag back.
 *
 * WHO SEES WHAT is decided by the server for the person asking
 * (GET /admin/osr/alerts). The live notice that prompts a refetch carries a
 * lead id and nothing else.
 *
 * Rendered inside the sitewide alert stack (SiteAlertCards), so lead cards
 * and announcements share one column and cannot cover each other or the
 * time clock bar. Dismissing hides that one card for this browser session.
 * On the lead page itself the cards stay out of the way: the page shows
 * the same leads.
 *
 *   POLL BUDGET  /alerts once a minute per open tab, only for people with
 *   the OSR permission, paused while the tab is hidden (usePoll). A live
 *   notice triggers it in between; the minute is the safety net.
 */
export default function OsrAlerts() {
  const { can, user } = useAuth();
  const allowed = !!user && can("osr.access");
  // "Pop up new leads" is a per-device switch (the bell on the OSR tab). Off,
  // the new and unclaimed cards stay away; the badge counts still update.
  const [prefs] = useOsrPrefs();
  const navigate = useNavigate();
  const location = useLocation();
  const [alerts, setAlerts] = useState(null);
  const [dismissed, setDismissed] = useState(readDismissed);
  const [busy, setBusy] = useState(null);
  const known = useRef(null); // lead ids already announced; null until the first answer
  const timer = useRef(null);

  const load = useCallback(async () => {
    try {
      const json = await osrFetch("/alerts", { skipSaveIndicator: true });
      const next = json.alerts;
      const ids = new Set((next.new || []).map((l) => l.id));
      if (known.current !== null && readOsrPrefs().sound) {
        const fresh = (next.new || []).some((l) => !known.current.has(l.id) && !l.mine);
        if (fresh) chime();
      }
      known.current = ids;
      setAlerts(next);
      publishNewLeadCount(next.new_count);
      return true;
    } catch {
      return false; // usePoll backs off; the last alerts stay up
    }
  }, []);

  usePoll(load, POLL_MS, { enabled: allowed });
  const onChanged = useCallback((msg) => {
    if (!allowed) return;
    // Typing, notes and enrichment never change what needs a person.
    if (msg && QUIET_ACTIONS.has(msg.action)) return;
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => { timer.current = null; load(); }, 400);
  }, [allowed, load]);
  useSocketEvent("osr:changed", onChanged);
  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);
  useEffect(() => { if (!allowed) publishNewLeadCount(null); }, [allowed]);

  const cards = [];
  if (alerts) {
    const add = (kind, lead, id = lead.id) => { if (!dismissed.has(`${kind}:${id}`)) cards.push({ kind, lead, key: `${kind}:${id}` }); };
    // Keyed by the request, not the lead: a third request shows again after
    // the card for the second was dismissed.
    (alerts.sheet_match || []).forEach((l) => add("sheet_match", l, `${l.id}:${l.sheet_match_id}`));
    (alerts.spam_rejected || []).forEach((l) => add("spam_rejected", l, `${l.id}:${l.spam_reviewed_at}`));
    (alerts.spam_review || []).forEach((l) => add("spam_review", l, `${l.id}:${l.spam_flagged_at}`));
    (alerts.repeat || []).forEach((l) => add("repeat", l, l.event_id));
    (alerts.follow_up || []).forEach((l) => add("follow_up", l));
    (alerts.uncontacted || []).forEach((l) => add("uncontacted", l));
    if (prefs.newLeads) {
      (alerts.new || []).forEach((l) => add("new", l));
      if (!(alerts.new || []).length) (alerts.unclaimed || []).forEach((l) => add("unclaimed", l));
    }
  }
  const ticking = cards.some((c) => c.kind === "new" || c.kind === "unclaimed");
  const now = useNow(1000, ticking);

  const onLeadPage = location.pathname.startsWith(OSR_PATH);
  if (!allowed || onLeadPage || cards.length === 0) return null;

  const dismiss = (key) => setDismissed((prev) => {
    const next = new Set(prev);
    next.add(key);
    writeDismissed(next);
    return next;
  });
  const open = (lead) => navigate(leadLink(lead.id));

  const capture = async (lead) => {
    if (busy) return;
    setBusy(lead.id);
    try {
      await osrFetch(`/leads/${lead.id}/capture`, { method: "POST" });
      showToast.success(`${lead.lead_name} is yours.`);
      navigate(leadLink(lead.id));
    } catch (err) {
      showToast.error(err.message || "Couldn't claim that lead.");
    } finally {
      setBusy(null);
      load();
    }
  };

  const shown = cards.slice(0, MAX_CARDS);
  const more = cards.length - shown.length;

  return (
    <>
      {shown.map(({ kind, lead, key }) => {
        const waited = secondsSince(lead.created_at, now);
        const level = kind === "new" || kind === "unclaimed" ? waitingLevel(waited) : "med";
        const from = handedInBy(lead);
        return (
          <div key={key} className="osr-alert" data-kind={kind} data-level={level}>
            <span className="osr-alert-icon" aria-hidden="true">
              <TagIcon name={kind === "follow_up" ? "calendar" : kind === "uncontacted" ? "phone" : kind === "repeat" ? "message" : kind === "sheet_match" ? "document" : kind.startsWith("spam") ? "shield" : "bell"} size={17} />
            </span>
            <div className="osr-alert-body">
              <div className="osr-alert-title">
                {kind === "new" && "New lead waiting"}
                {kind === "unclaimed" && "Lead still unclaimed"}
                {kind === "uncontacted" && "Claimed, not called yet"}
                {kind === "follow_up" && "Follow-up due"}
                {kind === "repeat" && "Wrote in again"}
                {kind === "sheet_match" && "Sales Sheet for your lead?"}
                {kind === "spam_review" && "Spam flag to review"}
                {kind === "spam_rejected" && "Not spam — back to you"}
              </div>
              <div className="osr-alert-name">{lead.lead_name}</div>
              <div className="osr-alert-meta">
                {(kind === "new" || kind === "unclaimed") && (
                  <>
                    <span>{sourceLabel(lead.source)}{from ? ` · from ${from}` : ""}{lead.location_name ? ` · ${lead.location_name}` : ""}</span>
                    <span className="osr-wait" data-level={level}>{fmtSpan(waited)} waiting</span>
                  </>
                )}
                {kind === "uncontacted" && <span>You claimed it {fmtSpan(lead.claimed_seconds)} ago.</span>}
                {kind === "follow_up" && <span>You set this follow-up. It is due now.</span>}
                {kind === "repeat" && <span>Your customer sent another request {fmtSpan(lead.seconds_ago)} ago. It is on the lead's timeline.</span>}
                {kind === "sheet_match" && <span>{lead.sheet_by_name || "Someone else"} sent a Sales Sheet for this customer {fmtSpan(lead.seconds_ago)} ago. It was not marked Sold: open the lead to say whether it is this lead's sale.</span>}
                {kind === "spam_review" && <span>Flagged by {lead.flagged_by_name || "someone"} {fmtSpan(lead.seconds_ago)} ago — {lead.spam_reason || "no reason"}{lead.spam_location ? ` (${lead.spam_location})` : ""}.</span>}
                {kind === "spam_rejected" && <span>{lead.reviewed_by_name || "A manager"} decided it is not spam{lead.spam_review_note ? ` — ${lead.spam_review_note}` : ""}. Work it to Sold, Estimate, or Lost.</span>}
              </div>
              <div className="osr-alert-actions">
                {kind === "new" && (
                  <button type="button" className="osr-alert-btn osr-alert-btn-main" disabled={busy === lead.id} onClick={() => capture(lead)}>
                    {busy === lead.id ? "Capturing…" : "Capture"}
                  </button>
                )}
                {kind === "new" || kind === "unclaimed" ? (
                  <button type="button" className="osr-alert-btn" onClick={() => navigate(OSR_PATH)}>{OSR_WORDING.openBoard}</button>
                ) : (
                  <button type="button" className="osr-alert-btn osr-alert-btn-main" onClick={() => open(lead)}>Open lead</button>
                )}
              </div>
            </div>
            <button type="button" className="osr-alert-close" onClick={() => dismiss(key)} aria-label={`Dismiss alert for ${lead.lead_name}`} title="Dismiss">
              <TagIcon name="x" size={14} strokeWidth={2.4} />
            </button>
          </div>
        );
      })}
      {more > 0 && (
        <button type="button" className="osr-alert osr-alert-more" onClick={() => navigate(OSR_PATH)}>
          +{more} more waiting · {OSR_WORDING.openBoard}
        </button>
      )}
    </>
  );
}
